export const getCroppedImg = (imageSrc, croppedAreaPixels) => {
    console.log("getCroppedImg called with:", {
        imageSrcType: typeof imageSrc,
        imageSrcLength: imageSrc ? imageSrc.length : 0,
        imageSrcStartsWith: imageSrc ? imageSrc.substring(0, 30) + '...' : 'null',
        croppedAreaPixels
    });
    
    // Detect Android platform
    const isAndroid = /Android/.test(navigator.userAgent);
    console.log("Platform detection - Android:", isAndroid);
    
    return new Promise((resolve, reject) => {
        if (!imageSrc || !croppedAreaPixels) {
            console.error("Invalid parameters:", { imageSrc: !!imageSrc, croppedAreaPixels: !!croppedAreaPixels });
            reject(new Error('Invalid parameters: image source or crop area is missing'));
            return;
        }

        // For Android compatibility - handle blob URLs differently
        const isObjectURL = imageSrc.startsWith('blob:');
        const isDataURL = imageSrc.startsWith('data:');
        console.log("Image source type:", { isObjectURL, isDataURL });
        
        // For very large images on Android, use a more direct approach
        if (isAndroid && isObjectURL) {
            console.log("Using optimized Android approach for blob URL");
            createOptimizedCrop(imageSrc, croppedAreaPixels, resolve, reject);
            return;
        }
        
        const image = new Image();
        
        // Only set crossOrigin for non-blob URLs to avoid CORS issues on Android
        if (!isObjectURL && !isDataURL) {
            console.log("Setting crossOrigin attribute");
            image.setAttribute('crossOrigin', 'anonymous');
        }
        
        // Set up error handling for image loading
        const onError = (error) => {
            console.error('Error loading image for cropping:', error);
            reject(new Error('Failed to load image for cropping'));
        };

        // Set up timeout to prevent hanging
        const timeoutId = setTimeout(() => {
            console.error("Image loading timed out");
            image.onload = null;
            image.onerror = null;
            reject(new Error('Image loading timed out'));
        }, 20000); // 20 second timeout

        image.onload = () => {
            console.log("Image loaded successfully in cropping function, dimensions:", image.width, "x", image.height);
            clearTimeout(timeoutId);
            
            try {
                console.log("Creating canvas for cropping");
                const canvas = document.createElement('canvas');
                const ctx = canvas.getContext('2d');

                if (!ctx) {
                    console.error("Failed to get canvas context");
                    reject(new Error('Could not get canvas context'));
                    return;
                }

                // Set proper dimensions with size limits to prevent memory issues on mobile
                // Use smaller dimensions for Android
                const maxDimension = isAndroid ? 1000 : 1600;
                let width = croppedAreaPixels.width;
                let height = croppedAreaPixels.height;
                
                console.log("Original crop dimensions:", width, "x", height);
                
                // Scale down if necessary
                if (width > maxDimension || height > maxDimension) {
                    const ratio = width / height;
                    if (width > height) {
                        width = maxDimension;
                        height = width / ratio;
                    } else {
                        height = maxDimension;
                        width = height * ratio;
                    }
                    console.log("Scaled dimensions:", width, "x", height);
                }
                
                // Ensure dimensions are integers to avoid Android rendering issues
                width = Math.floor(width);
                height = Math.floor(height);
                console.log("Final canvas dimensions:", width, "x", height);
                
                canvas.width = width;
                canvas.height = height;

                // Draw image with white background for transparency support
                ctx.fillStyle = 'white';
                ctx.fillRect(0, 0, canvas.width, canvas.height);
                
                // Draw the image with proper scaling
                console.log("Drawing image on canvas with params:", {
                    sourceX: Math.floor(croppedAreaPixels.x),
                    sourceY: Math.floor(croppedAreaPixels.y),
                    sourceWidth: Math.floor(croppedAreaPixels.width),
                    sourceHeight: Math.floor(croppedAreaPixels.height),
                    destX: 0,
                    destY: 0,
                    destWidth: width,
                    destHeight: height
                });
                
                try {
                    ctx.drawImage(
                        image,
                        Math.floor(croppedAreaPixels.x),
                        Math.floor(croppedAreaPixels.y),
                        Math.floor(croppedAreaPixels.width),
                        Math.floor(croppedAreaPixels.height),
                        0,
                        0,
                        width,
                        height
                    );
                    console.log("Image drawn on canvas successfully");
                } catch (drawError) {
                    console.error("Error drawing image on canvas:", drawError);
                    throw drawError;
                }

                // For Android, use a more direct approach
                if (isAndroid) {
                    try {
                        console.log("Using Android-specific approach");
                        // Use a simpler, more direct approach for Android
                        const dataUrl = canvas.toDataURL('image/jpeg', 0.80); // Lower quality for better performance
                        
                        // Convert directly to blob using manual method
                        const byteString = atob(dataUrl.split(',')[1]);
                        const mimeString = 'image/jpeg';
                        const ab = new ArrayBuffer(byteString.length);
                        const ia = new Uint8Array(ab);
                        
                        for (let i = 0; i < byteString.length; i++) {
                            ia[i] = byteString.charCodeAt(i);
                        }
                        
                        const file = new Blob([ab], { type: mimeString });
                        file.name = 'cropped-image.jpg';
                        file.lastModified = new Date().getTime();
                        console.log("Android approach: Final blob created:", file.size, "bytes");
                        
                        // Clean up to prevent memory leaks
                        canvas.width = 1;
                        canvas.height = 1;
                        
                        resolve(file);
                        return;
                    } catch (androidError) {
                        console.error("Android-specific approach failed:", androidError);
                        // Continue to standard approaches if Android-specific approach fails
                    }
                }

                // For non-Android or if Android-specific approach failed
                // Use dataURL approach first which is more reliable
                try {
                    console.log("Attempting to create dataURL from canvas");
                    const dataUrl = canvas.toDataURL('image/jpeg', 0.85);
                    console.log("DataURL created successfully, length:", dataUrl.length);
                    
                    // Convert data URL to Blob
                    console.log("Converting dataURL to blob via fetch");
                    fetch(dataUrl)
                        .then(res => {
                            console.log("Fetch response received:", res.status);
                            return res.blob();
                        })
                        .then(blob => {
                            console.log("Blob created from fetch:", blob.size, "bytes, type:", blob.type);
                            const file = new Blob([blob], { type: 'image/jpeg' });
                            file.name = 'cropped-image.jpg';
                            file.lastModified = new Date().getTime();
                            console.log("Final blob created:", file.size, "bytes");
                            
                            // Clean up to prevent memory leaks
                            canvas.width = 1;
                            canvas.height = 1;
                            
                            resolve(file);
                        })
                        .catch(fetchError => {
                            console.warn('Fetch blob from dataURL failed, trying alternative', fetchError);
                            
                            // Fallback method
                            console.log("Using manual ArrayBuffer fallback method");
                            const byteString = atob(dataUrl.split(',')[1]);
                            console.log("Decoded base64 string, length:", byteString.length);
                            const mimeString = dataUrl.split(',')[0].split(':')[1].split(';')[0];
                            const ab = new ArrayBuffer(byteString.length);
                            const ia = new Uint8Array(ab);
                            
                            for (let i = 0; i < byteString.length; i++) {
                                ia[i] = byteString.charCodeAt(i);
                            }
                            
                            const file = new Blob([ab], { type: mimeString });
                            file.name = 'cropped-image.jpg';
                            file.lastModified = new Date().getTime();
                            console.log("Final blob created via ArrayBuffer:", file.size, "bytes");
                            
                            // Clean up to prevent memory leaks
                            canvas.width = 1;
                            canvas.height = 1;
                            
                            resolve(file);
                        });
                } catch (dataUrlError) {
                    console.warn('toDataURL failed, trying toBlob', dataUrlError);
                    
                    // Fallback to toBlob as last resort
                    console.log("Using canvas.toBlob as last resort");
                    canvas.toBlob((blob) => {
                        if (!blob) {
                            console.error("Canvas toBlob returned empty blob");
                            reject(new Error('Canvas is empty'));
                            return;
                        }
                        
                        console.log("Blob created from canvas.toBlob:", blob.size, "bytes, type:", blob.type);
                        const file = new Blob([blob], { type: 'image/jpeg' });
                        file.name = 'cropped-image.jpg';
                        file.lastModified = new Date().getTime();
                        console.log("Final blob created from toBlob:", file.size, "bytes");
                        
                        // Clean up to prevent memory leaks
                        canvas.width = 1;
                        canvas.height = 1;
                        
                        resolve(file);
                    }, 'image/jpeg', 0.85);
                }
            } catch (error) {
                console.error('Error during image cropping:', error);
                console.error('Error stack:', error.stack);
                reject(new Error('Failed to process image: ' + error.message));
            }
        };

        image.onerror = onError;
        
        // For Android, ensure the image is fully loaded
        if (isAndroid && isObjectURL) {
            // For blob URLs on Android, we need a longer delay
            console.log("Using longer timeout for Android blob URL loading");
            setTimeout(() => {
                console.log("Setting image.src after Android timeout");
                image.src = imageSrc;
            }, 100); // 100ms for Android
        } else if (isObjectURL) {
            // For blob URLs on other platforms
            console.log("Using timeout for blob URL loading");
            setTimeout(() => {
                console.log("Setting image.src after timeout");
                image.src = imageSrc;
            }, 50);
        } else {
            // For regular URLs
            console.log("Setting image.src directly");
            image.src = imageSrc;
        }
    });
};

// Optimized approach for Android with large images
const createOptimizedCrop = (imageSrc, croppedAreaPixels, resolve, reject) => {
    console.log("Using optimized crop approach for Android");
    
    const img = new Image();
    img.onload = () => {
        console.log("Image loaded in optimized crop, dimensions:", img.width, "x", img.height);
        
        try {
            // Create a smaller canvas for better performance
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            
            if (!ctx) {
                console.error("Failed to get canvas context in optimized crop");
                reject(new Error('Could not get canvas context'));
                return;
            }
            
            // Limit dimensions for better performance
            const maxDimension = 800; // Even smaller for optimized approach
            let width = croppedAreaPixels.width;
            let height = croppedAreaPixels.height;
            
            // Scale down if necessary
            if (width > maxDimension || height > maxDimension) {
                const ratio = width / height;
                if (width > height) {
                    width = maxDimension;
                    height = width / ratio;
                } else {
                    height = maxDimension;
                    width = height * ratio;
                }
            }
            
            // Ensure dimensions are integers
            width = Math.floor(width);
            height = Math.floor(height);
            
            canvas.width = width;
            canvas.height = height;
            
            // Fill with white background
            ctx.fillStyle = 'white';
            ctx.fillRect(0, 0, width, height);
            
            // Draw the cropped image
            ctx.drawImage(
                img,
                Math.floor(croppedAreaPixels.x),
                Math.floor(croppedAreaPixels.y),
                Math.floor(croppedAreaPixels.width),
                Math.floor(croppedAreaPixels.height),
                0,
                0,
                width,
                height
            );
            
            // Convert to JPEG with lower quality for Android
            canvas.toBlob((blob) => {
                if (!blob) {
                    console.error("Failed to create blob in optimized crop");
                    reject(new Error('Failed to create blob'));
                    return;
                }
                
                const file = new Blob([blob], { type: 'image/jpeg' });
                file.name = 'cropped-image.jpg';
                file.lastModified = new Date().getTime();
                
                console.log("Optimized crop created blob:", file.size, "bytes");
                
                // Clean up
                canvas.width = 1;
                canvas.height = 1;
                
                resolve(file);
            }, 'image/jpeg', 0.75); // Lower quality for Android
        } catch (error) {
            console.error("Error in optimized crop:", error);
            reject(error);
        }
    };
    
    img.onerror = (error) => {
        console.error("Error loading image in optimized crop:", error);
        reject(new Error('Failed to load image for optimized cropping'));
    };
    
    // Set source with a small delay for Android
    setTimeout(() => {
        img.src = imageSrc;
    }, 50);
};
